// Copyright 2026. WebPros International GmbH. All rights reserved.

import React from 'react';
import {
    Badge,
    Button,
    Form,
    Pagination,
    Popover,
    Section,
    Toolbar,
    ToolbarExpander,
    ToolbarGroup,
    Translate,
} from '@plesk/ui-library';
import { HIGH_AVAILABILITY_HISTORY } from 'admin/highAvailability/constants/test';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import { ICONS } from 'common/constants';
import { PropsValue } from 'react-select';
import { ISelectRequiredOption } from 'common/components';
import { HighAvailabilityHistoryStatusFilter } from 'admin/common/components/Filters/HighAvailabilityHistoryStatusFilter/HighAvailabilityHistoryStatusFilter';
import {
    ComputeResourceFilter,
    IComputeResourceFilterOptionType,
} from 'admin/common/components/Filters/ComputeResourceFilter/ComputeResourceFilter';
import { VerticalFiltersContainer } from 'admin/common/styles/Styles';
import { IMeta } from 'common/api/resources/Response';

interface IHighAvailabilityOperationsProps {
    canMoveBack: boolean;
    isMovingBack: boolean;
    canRetry: boolean;
    isRetrying: boolean;
    onMoveBack: () => void;
    onRetry: () => void;
    retryTooltip: React.ReactNode;
    moveBackTooltip: React.ReactNode;
    selectedStatus?: ISelectRequiredOption;
    onSelectedStatusFilterChange: (option: PropsValue<ISelectRequiredOption>) => void;
    selectedCr?: IComputeResourceFilterOptionType;
    onSelectedCrFilterChange: (option: PropsValue<IComputeResourceFilterOptionType>) => void;
    filteredCount: number;
    meta?: IMeta;
    loadItems?: (page: number) => void;
    isLoading?: boolean;
}

export const HighAvailabilityOperations: React.FC<IHighAvailabilityOperationsProps> = ({
    canMoveBack,
    isMovingBack,
    canRetry,
    isRetrying,
    onMoveBack,
    onRetry,
    retryTooltip,
    moveBackTooltip,
    selectedStatus,
    onSelectedStatusFilterChange,
    selectedCr,
    onSelectedCrFilterChange,
    filteredCount,
    meta,
    loadItems,
    isLoading,
}) => (
    <Toolbar>
        {filteredCount > 0 && meta && (
            <span>
                <Translate
                    content="highAvailability.activities.operations.itemsFound"
                    params={{ count: meta.total }}
                />
            </span>
        )}
        <ToolbarGroup title="ha-retry-action">
            <ButtonWithConfirmation
                data-cy={HIGH_AVAILABILITY_HISTORY.RETRY_BUTTON}
                icon={ICONS.REFRESH}
                confirmationButtonGhost={false}
                disabled={!canRetry}
                isLoading={isRetrying}
                confirmationButtonText={<Translate content="highAvailability.activities.operations.retryButton" />}
                handleConfirm={onRetry}
                translations={{
                    title: <Translate content="highAvailability.activities.operations.retryConfirmation.title" />,
                    button: <Translate content="highAvailability.activities.operations.retryConfirmation.button" />,
                    text: <Translate content="highAvailability.activities.operations.retryConfirmation.text" />,
                    tooltip: retryTooltip,
                }}
            />
        </ToolbarGroup>
        <ToolbarGroup title="ha-move-back-action">
            <ButtonWithConfirmation
                data-cy={HIGH_AVAILABILITY_HISTORY.MOVE_BACK_BUTTON}
                icon={ICONS.ARROW_BACK}
                confirmationButtonGhost={false}
                disabled={!canMoveBack}
                isLoading={isMovingBack}
                confirmationButtonText={<Translate content="highAvailability.activities.operations.moveBackButton" />}
                handleConfirm={onMoveBack}
                translations={{
                    title: <Translate content="highAvailability.activities.operations.moveBackConfirmation.title" />,
                    button: <Translate content="highAvailability.activities.operations.moveBackConfirmation.button" />,
                    text: <Translate content="highAvailability.activities.operations.moveBackConfirmation.text" />,
                    tooltip: moveBackTooltip,
                }}
            />
        </ToolbarGroup>
        <ToolbarExpander />
        {meta && loadItems && (
            <Pagination
                itemsPerPage={meta.per_page}
                itemsPerPageOptions={[50, 'all']}
                current={meta.current_page}
                total={meta.last_page}
                onSelect={loadItems}
                loading={isLoading}
            />
        )}
        <Popover
            canCloseOnOutsideClick={false}
            target={(
                <Badge label={filteredCount} hidden={!filteredCount}>
                    <Button
                        icon={ICONS.FILTER}
                        data-cy={HIGH_AVAILABILITY_HISTORY.FILTERS_BUTTON}
                    >
                        <Translate content="highAvailability.activities.operations.filters" />
                    </Button>
                </Badge>
            )}
            placement="left-bottom"
        >
            <Section title={<Translate content="highAvailability.activities.operations.filters" />}>
                <Form
                    submitButton={false}
                    cancelButton={false}
                    applyButton={false}
                    vertical={true}
                    footerClassName="hidden"
                >
                    <VerticalFiltersContainer>
                        <HighAvailabilityHistoryStatusFilter
                            onChange={onSelectedStatusFilterChange}
                            value={selectedStatus}
                        />
                        <ComputeResourceFilter
                            onChange={onSelectedCrFilterChange}
                            value={selectedCr}
                        />
                    </VerticalFiltersContainer>
                </Form>
            </Section>
        </Popover>
    </Toolbar>
);
